import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const PAGE_SIZE = 12;

// Server-side paging/search/category filter (BACKEND.md §1) against
// GET /inventory — the same endpoint the admin Inventory page uses, now
// with an added categoryId filter (Step 7) so the customer catalogue never
// loads the full company list to filter client-side.
const useBrowseProductsPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const [categories, setCategories] = useState([]);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!config?.backendUrl || !companyId) return;
    axios.get(`${config.backendUrl}/categories?companyId=${companyId}`)
      .then((res) => setCategories(res.data))
      .catch((error) => console.error('Error fetching categories', error));
  }, [config, companyId]);

  const fetchProducts = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (categoryFilter !== 'all') params.categoryId = categoryFilter;
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/inventory`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching products', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /inventory · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, categoryFilter, search, t]);

  useEffect(() => { fetchProducts(true); }, [fetchProducts]);

  const loadMore = () => fetchProducts(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : (search || categoryFilter !== 'all');

  return {
    t, companyId, categories,
    total, errorInfo, effectiveStatus, effectiveRows, isNoMatch,
    search, setSearch, searchInput, setSearchInput,
    categoryFilter, setCategoryFilter,
    loadingMore, loadMore, remaining, nextBatchSize,
    fetchProducts,
  };
};

export default useBrowseProductsPage;
